import { useEffect, useRef, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useFamilyStore } from '../stores/familyStore'
import { useAuthStore } from '../stores/authStore'
import { format } from 'date-fns'

interface NoteChange {
  isNew: boolean
  date: string
  title: string
  content: string | null
}

interface ExpensesSummary {
  count: number
  totalAmount: number
  currency: string
}

interface RecentChangesData {
  hasChanges: boolean
  notes: NoteChange[]
  expenses: ExpensesSummary | null
  careChangesCount: number
  isLoading: boolean
  markAsSeen: () => void
  refetch: () => void
}

const STORAGE_KEY_PREFIX = 'recent-changes-seen-'
const DEFAULT_LOOKBACK_HOURS = 24

function getLastSeen(familyId: string): string {
  const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${familyId}`)
  if (stored) return stored
  // Default: 24h ago
  const fallback = new Date(Date.now() - DEFAULT_LOOKBACK_HOURS * 60 * 60 * 1000)
  return fallback.toISOString()
}

export function useRecentChanges(): RecentChangesData {
  const { family, members } = useFamilyStore()
  const user = useAuthStore(s => s.user)
  const [isLoading, setIsLoading] = useState(false)
  const [notes, setNotes] = useState<NoteChange[]>([])
  const [expenses, setExpenses] = useState<ExpensesSummary | null>(null)
  const [careChangesCount, setCareChangesCount] = useState(0)
  const fetchedRef = useRef(false)

  // Powiadomienia tylko dla rodziców — obserwatorzy nie otrzymują powiadomień
  const currentMember = members.find(m => m.user_id === user?.id)
  const isParent = currentMember?.role === 'parent_a' || currentMember?.role === 'parent_b'

  const otherParent = members.find(
    m => (m.role === 'parent_a' || m.role === 'parent_b') && m.user_id !== user?.id
  )

  const familyId = family?.id
  const otherParentId = isParent ? otherParent?.user_id : undefined

  const fetchChanges = useCallback(async () => {
    if (!familyId || !otherParentId || !user) return

    const lastSeen = getLastSeen(familyId)

    setIsLoading(true)
    try {
      // Two queries in parallel
      const [eventsResult, entriesResult] = await Promise.all([
        // 1. Events: notes + expenses
        supabase
          .from('events')
          .select('id, type, title, notes, start_at, amount, currency, created_at, updated_at')
          .eq('family_id', familyId)
          .eq('created_by', otherParentId)
          .gt('updated_at', lastSeen)
          .in('type', ['note', 'expense'])
          .order('updated_at', { ascending: false })
          .limit(20),

        // 2. Calendar entries: just count
        supabase
          .from('calendar_entries')
          .select('id', { count: 'exact', head: true })
          .eq('family_id', familyId)
          .eq('created_by', otherParentId)
          .gt('updated_at', lastSeen)
      ])

      // Process notes
      const eventData = eventsResult.data || []
      const noteEvents = eventData.filter(e => e.type === 'note')
      const expenseEvents = eventData.filter(e => e.type === 'expense')

      const processedNotes: NoteChange[] = noteEvents.map(e => ({
        isNew: e.created_at > lastSeen,
        date: format(new Date(e.start_at), 'dd.MM'),
        title: e.title || '',
        content: e.notes || null
      }))

      // Process expenses
      let expensesSummary: ExpensesSummary | null = null
      if (expenseEvents.length > 0) {
        const totalAmount = expenseEvents.reduce((sum, e) => sum + (e.amount || 0), 0)
        const currency = expenseEvents[0]?.currency || 'PLN'
        expensesSummary = {
          count: expenseEvents.length,
          totalAmount,
          currency
        }
      }

      // Process care changes
      const careCount = entriesResult.count || 0

      setNotes(processedNotes)
      setExpenses(expensesSummary)
      setCareChangesCount(careCount)
    } catch (err) {
      console.error('Error fetching recent changes:', err)
    } finally {
      setIsLoading(false)
    }
  }, [familyId, otherParentId, user])

  const markAsSeen = useCallback(() => {
    if (!familyId) return
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${familyId}`, new Date().toISOString())
    setNotes([])
    setExpenses(null)
    setCareChangesCount(0)
  }, [familyId])

  // Initial fetch
  useEffect(() => {
    if (!familyId || !otherParentId || fetchedRef.current) return
    fetchedRef.current = true
    void fetchChanges()
  }, [familyId, otherParentId, fetchChanges])

  const hasChanges = notes.length > 0 || expenses !== null || careChangesCount > 0

  return {
    hasChanges,
    notes,
    expenses,
    careChangesCount,
    isLoading,
    markAsSeen,
    refetch: fetchChanges
  }
}
